import { createFileRoute } from '@tanstack/react-router';
import {
  createColumnHelper, flexRender, getCoreRowModel, PaginationState, useReactTable
} from '@tanstack/react-table';
import { DateTime } from 'luxon';
import React from 'react';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { requireAuth } from '@/app/lib/auth-guard';
import { ApplicationsListItem } from '@/app/network/models/v3/applications';
import { formatApplicationStatus } from '@/applications/applications';
import { useApplicationsList } from '@/applications/queries';
import { Button } from '@/common/components/button';
import { Heading } from '@/common/components/heading';
import { Nullish } from '@/common/components/nullish';
import { Spinner } from '@/common/components/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/common/components/table';
import { TablePagination } from '@/common/components/table-pagination';
import { FileLinesIcon } from '@/icons/fa/duotone-solid';
import { ContentSection } from '@/shared/components/content-section';
import { EmptyState, EmptyStateMessage } from '@/shared/components/empty-states';
import { LiveData } from '@/shared/components/live-data';
import { SectionHeader } from '@/shared/components/sections';
import { Toast } from '@/shared/components/toast';
import { seo } from '@/shared/seo';

const applicationsSearchSchema = z.object({
  page: z.number().int().positive().nullish(),
  rpp: z.number().int().positive().nullish()
});

export const Route = createFileRoute('/applications/')({
  beforeLoad: async (ctx) => {
    await requireAuth(ctx);
  },
  validateSearch: applicationsSearchSchema,
  head: () => ({
    meta: seo({ title: 'My Applications' })
  }),
  component: RouteComponent,
  staticData: {
    headerSection: null
  }
});

const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_ROWS_PER_PAGE = 5;

const columnHelper = createColumnHelper<ApplicationsListItem>();

function RouteComponent() {
  const searchParams = Route.useSearch();
  const page = searchParams.page ?? DEFAULT_PAGE_NUMBER;
  const { data: applicationsData, isPending, error } = useApplicationsList({ page, rpp: DEFAULT_ROWS_PER_PAGE });

  React.useEffect(() => {
    if (error) {
      toast.error(Toast, {
        data: {
          title: 'Unable to Load Applications',
          content: error?.message ?? 'Please try again later.'
        }
      });
    }
  }, [error]);

  return (
    <main className="flex flex-col md:flex-row h-full w-full p-0">
      <ContentSection bg="white" fullWidth>
        <SectionHeader>
          <Heading>My Applications</Heading>
          <Button to="/apply">New Application</Button>
        </SectionHeader>

        <LiveData
          data={applicationsData?.items}
          isLoading={isPending}
          loadingState={<Spinner />}
          fallback={<ApplicationsEmptyState />}
        >
          {data => (
            <ApplicationsTable
              data={data}
              totalResults={applicationsData?.totalResults ?? 0}
              pagination={{ pageIndex: page, pageSize: DEFAULT_ROWS_PER_PAGE }}
            />
          )}
        </LiveData>
      </ContentSection>
    </main>
  );
}

const columns = [
  columnHelper.accessor('serviceLocation', {
    cell: info => <Nullish value={info.getValue()} />,
    header: () => 'Service Location'
  }),
  columnHelper.accessor('status', {
    cell: info => formatApplicationStatus(info.getValue()),
    header: () => 'Status'
  }),
  columnHelper.accessor('createdAt', {
    cell: info => DateTime.fromISO(info.getValue()).toLocaleString(),
    header: () => 'Date Submitted'
  }),
  columnHelper.display({
    id: 'actions',
    cell: ({ row }) => (
      <Button plain>
        {/* TODO add actual continue next steps functionality */}
        {row.getValue('status') === 'worked_by_applicant' ? 'Continue' : 'Next Steps'}
      </Button>
    )
  })
];

type ApplicationsTableProps = {
  data: ApplicationsListItem[];
  totalResults: number;
  pagination: PaginationState;
};

function ApplicationsTable({ data, totalResults, pagination }: ApplicationsTableProps) {
  const table = useReactTable({
    data,
    rowCount: totalResults,
    columns,
    state: { pagination },
    manualPagination: true,
    getCoreRowModel: getCoreRowModel()
  });

  return (
    <div className="-mx-4 mt-8 sm:-mx-0">
      <Table>
        <TableHead>
          {
            table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <TableHeader key={header.id}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHeader>
                ))}
              </TableRow>
            ))
          }
        </TableHead>
        <TableBody>
          {table.getRowModel().rows.map(row => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map(cell => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        className="mt-4"
        page={table.getState().pagination.pageIndex}
        pageSize={table.getState().pagination.pageSize}
        totalResults={totalResults}
        searchParams={new URLSearchParams()}
        totalPages={table.getPageCount()}
      />
    </div>
  );
}

function ApplicationsEmptyState() {
  return (
    <EmptyState>
      <FileLinesIcon className="size-20 text-gray-300"/>
      <Heading>No Applications</Heading>
      <EmptyStateMessage>
        You have no applications yet. Create one to get started.
      </EmptyStateMessage>
    </EmptyState>
  );
}
